/**
 * Store links with install attribution (FIN-742).
 *
 * The page's UTM params become an Attribution (source/campaign), which is
 * encoded into both store URLs:
 * - Apple: `pt` (provider token, hardcoded) + `ct` (campaign token, ≤30 chars) + `mt=8`
 *   → App Store Connect › App Analytics › Sources › Campaigns.
 * - Google: `referrer` = URL-encoded "utm_source=…&utm_campaign=…", read in
 *   the app via the Play Install Referrer API and sent to the backend as
 *   acquisition_source / acquisition_campaign.
 *
 * Normalisation mirrors the backend contract (source lower-cased ≤64,
 * campaign ≤128). Junk never breaks a link — it falls back to defaults.
 */

import { APP_CONFIG } from './config'

export type QueryParams =
  | URLSearchParams
  | Record<string, string | string[] | undefined>
  | null
  | undefined

export interface Attribution {
  /** utm_source slug, lower-cased, ≤64 chars; DEFAULT_SOURCE when absent. */
  source: string
  /** utm_campaign slug, case kept, ≤128 chars; null when absent. */
  campaign: string | null
}

export interface StoreLinks {
  appStore: string
  playStore: string
}

export interface StoreLinkConfig {
  appStoreUrl: string
  appleProviderToken: string
  playStoreUrl: string
}

export const DEFAULT_SOURCE = 'website'
/** Apple provider token for App Store campaign links (public value from App Store Connect). */
export const APPLE_PROVIDER_TOKEN = '128274344'
export const APPLE_CT_MAX_LEN = 30
const SOURCE_MAX_LEN = 64
const CAMPAIGN_MAX_LEN = 128

const DEFAULT_CONFIG: StoreLinkConfig = {
  appStoreUrl: APP_CONFIG.downloads.ios.appStoreUrl,
  appleProviderToken: APPLE_PROVIDER_TOKEN,
  playStoreUrl: APP_CONFIG.downloads.android.playStoreUrl,
}

/** First value of a query param from either URLSearchParams or a Next searchParams record. */
export function readParam(params: QueryParams, key: string): string | null {
  if (!params) return null
  if (params instanceof URLSearchParams) return params.get(key)
  const raw = params[key]
  return (Array.isArray(raw) ? raw[0] : raw) ?? null
}

function slug(raw: string | null, maxLen: number): string | null {
  if (!raw) return null
  const value = raw
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^A-Za-z0-9._-]/g, '')
    .slice(0, maxLen)
  return value || null
}

export function attributionFromParams(params: QueryParams): Attribution {
  const source = slug(readParam(params, 'utm_source'), SOURCE_MAX_LEN)?.toLowerCase() ?? DEFAULT_SOURCE
  const campaign = slug(readParam(params, 'utm_campaign'), CAMPAIGN_MAX_LEN)
  return { source, campaign }
}

/** Apple campaign token: "source" or "source-campaign", cut to 30 chars. */
export function appleCampaignToken(a: Attribution): string {
  const token = a.campaign ? `${a.source}-${a.campaign}` : a.source
  const cut = token.slice(0, APPLE_CT_MAX_LEN)
  // Don't leave a dangling separator where the cut landed.
  return cut.replace(/[-._]+$/, '') || a.source.slice(0, APPLE_CT_MAX_LEN)
}

export function buildAppStoreUrl(a: Attribution, config: StoreLinkConfig = DEFAULT_CONFIG): string {
  const url = new URL(config.appStoreUrl)
  url.searchParams.set('pt', config.appleProviderToken)
  url.searchParams.set('ct', appleCampaignToken(a))
  url.searchParams.set('mt', '8')
  return url.toString()
}

export function buildPlayStoreUrl(a: Attribution, config: StoreLinkConfig = DEFAULT_CONFIG): string {
  const referrer = new URLSearchParams({ utm_source: a.source })
  if (a.campaign) referrer.set('utm_campaign', a.campaign)
  const url = new URL(config.playStoreUrl)
  url.searchParams.set('referrer', referrer.toString())
  return url.toString()
}

export function buildStoreLinks(a: Attribution, config: StoreLinkConfig = DEFAULT_CONFIG): StoreLinks {
  return { appStore: buildAppStoreUrl(a, config), playStore: buildPlayStoreUrl(a, config) }
}
